import api from './api';

/**
 * Property Measurement API Service
 * Handles all satellite measurement related API calls
 */
const measurementApi = {
  /**
   * Get all measurements for the business
   */
  getAll: async () => {
    const response = await api.get('/propertymeasurements');
    return response.data;
  },

  /**
   * Get a specific measurement by ID
   */
  getById: async (id) => {
    const response = await api.get(`/propertymeasurements/${id}`);
    return response.data;
  },

  /**
   * Get all measurements for a customer
   */
  getByCustomer: async (customerId) => {
    const response = await api.get(`/propertymeasurements/customer/${customerId}`);
    return response.data;
  },

  /**
   * Get measurement linked to a job
   */
  getByJob: async (jobId) => {
    const response = await api.get(`/propertymeasurements/job/${jobId}`);
    return response.data;
  },

  /**
   * Create a new measurement with zones
   */
  create: async (measurementData) => {
    const response = await api.post('/propertymeasurements', measurementData);
    return response.data;
  },

  /**
   * Update an existing measurement
   */
  update: async (id, measurementData) => {
    const response = await api.put(`/propertymeasurements/${id}`, {
      id,
      ...measurementData
    });
    return response.data;
  },

  /**
   * Delete a measurement
   */
  delete: async (id) => {
    await api.delete(`/propertymeasurements/${id}`);
    return true;
  },

  /**
   * Calculate quote from measurement zones
   */
  calculateQuote: async (measurementId, zoneOverrides = null) => {
    const response = await api.post(`/propertymeasurements/${measurementId}/calculate-quote`, {
      measurementId,
      zoneOverrides
    });
    return response.data;
  },

  /**
   * Save screenshot URL for a measurement
   */
  saveScreenshot: async (measurementId, screenshotUrl) => {
    const response = await api.post(`/propertymeasurements/${measurementId}/screenshot`, {
      screenshotUrl
    });
    return response.data;
  }
};

// Zone type constants and utilities
export const ZONE_TYPES = {
  Driveway: { label: 'Driveway', color: '#6B7280', icon: '🚗' },
  Sidewalk: { label: 'Sidewalk', color: '#9CA3AF', icon: '🚶', linear: true },
  Patio: { label: 'Patio', color: '#F59E0B', icon: '☀️' },
  Deck: { label: 'Deck', color: '#92400E', icon: '🪵' },
  Pool: { label: 'Pool Deck', color: '#06B6D4', icon: '🏊' },
  House: { label: 'House', color: '#3B82F6', icon: '🏠' },
  Roof: { label: 'Roof', color: '#EF4444', icon: '🏠' },
  Fence: { label: 'Fence', color: '#78350F', icon: '🪵', linear: true },
  Gutter: { label: 'Gutter', color: '#10B981', icon: '🌧️', linear: true },
  Garage: { label: 'Garage Floor', color: '#4B5563', icon: '🚘' },
  Walkway: { label: 'Walkway', color: '#D1D5DB', icon: '👣' },
  Retaining: { label: 'Retaining Wall', color: '#78716C', icon: '🧱' },
  Other: { label: 'Other', color: '#8B5CF6', icon: '📐' }
};

export const getZoneColor = (zoneType) => {
  return ZONE_TYPES[zoneType]?.color || '#8B5CF6';
};

export const getZoneLabel = (zoneType) => {
  return ZONE_TYPES[zoneType]?.label || zoneType;
};

export const isLinearZone = (zoneType) => {
  return ZONE_TYPES[zoneType]?.linear || false;
};

/**
 * Convert square meters to square feet
 */
export const sqMetersToSqFeet = (sqMeters) => {
  return sqMeters * 10.7639;
};

/**
 * Convert meters to feet
 */
export const metersToFeet = (meters) => {
  return meters * 3.28084;
};

/**
 * Format square footage for display
 */
export const formatSqFt = (sqFt) => {
  if (!sqFt || sqFt === 0) return '0 sq ft';
  return `${sqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft`;
};

/**
 * Format linear footage for display
 */
export const formatLinearFt = (ft) => {
  if (!ft || ft === 0) return '0 ft';
  return `${ft.toLocaleString(undefined, { maximumFractionDigits: 0 })} ft`;
};

export default measurementApi;